import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import AdminNavBar from "../components/AdminNavBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faUser, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import Toast from "../components/Toast";
import {
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
} from "../components/AdminPageShell";
import { useAdminActivityLogs } from "../store/admin";
import ActivityLogFilters from "./components/activityLogs/ActivityLogFilters";
import ActivityLogTable from "./components/activityLogs/ActivityLogTable";
import StaffPagination from "./components/staff/StaffPagination";

// ===== ACTIVITY LOGS PAGE — GET /api/admin/activity-logs =====
// Lists all system activity logs with filterable columns and pagination.
// Individual log detail lives at /admin/activity-logs/:logId.
const ActivityLogs = () => {
  const navigate = useNavigate();

  const {
    activityLogs,
    activityLogsLoading,
    activityLogsError,
    activityLogTotal,
    activityLogPages,
    fetchActivityLogs,
  } = useAdminActivityLogs();

  const [isLoading, setIsLoading] = useState(true);
  const [isReloading, setIsReloading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sort, setSort] = useState("newest");
  const [filters, setFilters] = useState({
    actorType: "",
    action: "",
    targetType: "",
    from: "",
    to: "",
  });

  const [isSuccess, setIsSuccess] = useState(true);
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  // GET /api/admin/activity-logs — fires on page / sort / filters change.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsReloading(true);
      const result = await fetchActivityLogs({
        page,
        limit,
        sort,
        ...filters,
        ...(filters.from
          ? { from: new Date(`${filters.from}T00:00:00`).toISOString() }
          : {}),
        ...(filters.to
          ? { to: new Date(`${filters.to}T23:59:59`).toISOString() }
          : {}),
      });
      if (cancelled) return;
      if (!result.success) {
        showToast(result.error || "Failed to fetch activity logs", false);
      }
      setIsLoading(false);
      setIsReloading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [fetchActivityLogs, page, sort, filters, limit, showToast]);

  // ── Filter / sort helpers ──────────────────────────────────────
  const handleFilterChange = (field, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setPage(1);
    setSort("newest");
    setFilters({
      actorType: "",
      action: "",
      targetType: "",
      from: "",
      to: "",
    });
  };

  const handleSortChange = (value) => {
    setPage(1);
    setSort(value);
  };

  const handleView = (log) => {
    const id = log?.id ?? log?._id;
    if (id) {
      navigate(`/admin/activity-logs/${id}`);
    }
  };

  return (
    <div>
      <AdminNavBar
        title="Activity Logs"
        context="Audit trail of all system actions. Filter by actor, action, target, or date range."
      />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        {isLoading ? (
          <AdminLoadingState />
        ) : (
          <div className="sub-body space-y-6">
            {/* Scroll-to-top button */}
            <button
              className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-50 md:right-8"
              onClick={() => {
                const subBody = document.querySelector(".sub-body");
                if (subBody) {
                  subBody.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              aria-label="Scroll to top"
            >
              ▲
            </button>

            {/* Summary cards */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                {
                  label: "Total Logs",
                  value: activityLogTotal ?? 0,
                  icon: faList,
                  accent: "bg-indigo-100 text-indigo-600",
                },
                {
                  label: "Actor Filter",
                  value: filters.actorType || "Any",
                  icon: faUser,
                  accent: "bg-purple-100 text-purple-600",
                },
                {
                  label: "Total Pages",
                  value: activityLogPages ?? 1,
                  icon: faFileAlt,
                  accent: "bg-sky-100 text-sky-600",
                },
              ].map((card) => (
                <div
                  key={card.label}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm"
                >
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-base ${card.accent}`}
                  >
                    <FontAwesomeIcon icon={card.icon} />
                  </div>
                  <div>
                    <div className="text-xl font-bold leading-tight text-slate-900">
                      {card.value}
                    </div>
                    <div className="text-[12px] text-slate-500">{card.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <ActivityLogFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleReset}
              sort={sort}
              onSortChange={handleSortChange}
            />

            {/* Error banner */}
            {activityLogsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {activityLogsError}
              </div>
            )}

            {/* Section header + table */}
            <AdminSectionHeader
              title="Activity Logs"
              description="Audit trail of all system actions. Click View on a row for full details."
              count={`${activityLogTotal ?? activityLogs.length} entries`}
            />

            <AdminTablePanel className="mt-4">
              <ActivityLogTable
                logs={activityLogs}
                isLoading={isReloading || activityLogsLoading}
                sort={sort}
                onSortChange={handleSortChange}
                onView={handleView}
              />
            </AdminTablePanel>

            {/* Pagination */}
            {!isReloading && !activityLogsError && activityLogs.length !== 0 && (
              <div className="mt-4">
                <StaffPagination
                  page={page}
                  lastPage={activityLogPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </div>
        )}
      </AdminPageBody>
    </div>
  );
};

export default ActivityLogs;
  